import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { IUser } from "muminsmart-utils";
import { routes } from "@/lib/constants";
import {
  getSession as loadSession,
  login as authLogin,
  logout as authLogout,
} from "@/lib/api/auth-service";

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface AuthSession {
  user: IUser;
}

interface AuthContextValue {
  data: AuthSession | null;
  status: AuthStatus;
  login: (payload: {
    email?: string;
    mobile?: string;
    password: string;
  }) => Promise<{ ok: boolean; error?: string }>;
  logout: (callbackUrl?: string) => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: React.PropsWithChildren) {
  const [data, setData] = useState<AuthSession | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");

  const refreshSession = useCallback(async () => {
    const session = await loadSession();
    setData(session);
    setStatus(session ? "authenticated" : "unauthenticated");
  }, []);

  useEffect(() => {
    void refreshSession();
  }, [refreshSession]);

  const login = useCallback(
    async (payload: {
      email?: string;
      mobile?: string;
      password: string;
    }) => {
      try {
        const session = await authLogin(payload);
        setData(session);
        setStatus("authenticated");
        return { ok: true };
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Login failed";
        return {
          ok: false,
          error: message,
        };
      }
    },
    [],
  );

  const logout = useCallback(async (callbackUrl = routes.auth.login) => {
    await authLogout();
    setData(null);
    setStatus("unauthenticated");
    window.location.href = callbackUrl;
  }, []);

  const value = useMemo(
    () => ({
      data,
      status,
      login,
      logout,
      refreshSession,
    }),
    [data, status, login, logout, refreshSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}

/** Drop-in replacement for next-auth's useSession hook */
export function useSession() {
  const { data, status } = useAuth();

  return {
    data,
    status,
  };
}
